import { Link } from 'react-router-dom'
import { useI18n } from '../i18n'
import { useWorkYard, useMembers, useUpdateMemberRole, useRemoveMember } from '../api/hooks'
import type { FarmMember, FarmRole } from '@horsehub/shared'
import { ASSIGNABLE_FARM_ROLES } from '@horsehub/shared'
import { PageSpinner, EmptyState } from '../components/ui'
import { confirmDialog } from '../lib/confirm'
import { toast } from '../lib/toast'
import { errorMessage } from '../lib/errors'

/** Everyone on the yard — staff and liveries. Owners set roles; managers can remove. */
export function FarmMembers() {
  const { t } = useI18n()
  const { farmId, role, isLoading } = useWorkYard()
  const { data: members, isLoading: membersLoading } = useMembers(farmId)
  const updateRole = useUpdateMemberRole(farmId ?? '')
  const remove = useRemoveMember(farmId ?? '')

  if (isLoading || membersLoading) return <PageSpinner />
  if (!farmId) {
    return (
      <main className="mx-auto max-w-3xl px-5 py-16 text-center">
        <Link to="/farm" className="btn-primary mt-4 inline-block">{t('farm.billing.back')}</Link>
      </main>
    )
  }

  const isOwner = role === 'owner'
  const canRemove = role === 'owner' || role === 'manager'
  const staff = (members ?? []).filter((m) => m.role === 'manager' || m.role === 'groom')
  const liveries = (members ?? []).filter((m) => m.role === 'boarder')

  const onRemove = async (m: FarmMember) => {
    const ok = await confirmDialog({
      title: t('farm.members.remove'),
      message: t('farm.members.removeConfirm', { name: m.name }),
      confirmLabel: t('farm.members.remove'),
      cancelLabel: t('common.cancel'),
      danger: true,
    })
    if (ok) remove.mutate(m.userId, { onError: (e) => toast.error(errorMessage(e)) })
  }

  const Row = ({ m }: { m: FarmMember }) => (
    <li className="flex flex-wrap items-center justify-between gap-3 p-4">
      <div className="min-w-0">
        <div className="font-bold">{m.name}</div>
        <div className="truncate text-sm text-brand-600 dark:text-brand-300">
          {m.email}
          {typeof m.horseCount === 'number' && m.horseCount > 0 ? ` · ${t('farm.members.horsesHere', { n: m.horseCount })}` : ''}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {isOwner ? (
          <select
            value={m.role}
            aria-label={t('farm.members.role')}
            onChange={(e) =>
              updateRole.mutate(
                { userId: m.userId, role: e.target.value as FarmRole },
                { onError: (err) => toast.error(errorMessage(err)) },
              )
            }
            className="rounded-xl border border-brand-200 dark:border-brand-700 bg-white dark:bg-brand-900 px-3 py-2 text-sm outline-none focus:border-brand-500"
          >
            {ASSIGNABLE_FARM_ROLES.map((r) => (
              <option key={r} value={r}>{t(`farm.roles.${r}`)}</option>
            ))}
          </select>
        ) : (
          <span className="chip">{t(`farm.roles.${m.role}`)}</span>
        )}
        {canRemove && (
          <button onClick={() => onRemove(m)} className="btn-ghost !px-3 !py-2 text-sm text-red-600 dark:text-red-400">
            {t('farm.members.remove')}
          </button>
        )}
      </div>
    </li>
  )

  return (
    <main className="mx-auto max-w-3xl px-5 py-8 pb-24">
      <div className="flex items-center gap-3">
        <Link to="/farm" className="btn-ghost !px-3 !py-2 text-sm">←</Link>
        <h1 className="text-2xl font-extrabold tracking-tight">{t('farm.members.title')}</h1>
      </div>

      {(members ?? []).length === 0 ? (
        <div className="mt-6">
          <EmptyState icon="👥" title={t('farm.members.empty')} body={t('farm.members.emptyBody')} />
        </div>
      ) : (
        <>
          {staff.length > 0 && (
            <>
              <h2 className="mt-8 text-lg font-bold">{t('farm.members.staff')}</h2>
              <ul className="card mt-3 divide-y divide-brand-100 dark:divide-brand-700/60">
                {staff.map((m) => <Row key={m.userId} m={m} />)}
              </ul>
            </>
          )}
          <h2 className="mt-8 text-lg font-bold">{t('farm.members.liveries')}</h2>
          {liveries.length > 0 ? (
            <ul className="card mt-3 divide-y divide-brand-100 dark:divide-brand-700/60">
              {liveries.map((m) => <Row key={m.userId} m={m} />)}
            </ul>
          ) : (
            <p className="mt-3 text-sm text-brand-600 dark:text-brand-300">{t('farm.members.emptyBody')}</p>
          )}
        </>
      )}
    </main>
  )
}
